<template>
  <transition name="fade" appear>
    <div class="c-popup" v-show="modal" :style="{ position: isError?'fixed':'unset'}">
      <audio ref="audioPlayer" style="display: none;" @timeupdate="timeUpdate" @ended="toggleAudioBtnText" controls></audio>
      <template v-if="!isProcessing">
        <div class="c-message-layer" v-if="isError">
          <dialog class="c-message -alert">
            読み上げに失敗しました。<br>もう一度、「読み上げ」ボタンを押してください。
            <button @click="$emit('close')" class="c-message-close">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" class="c-message-close-icon">
                <path d="M11.9999 1.65661L10.3433 0L6 4.34327L1.65663 0L0 1.65684L4.3433 6.00014L0 10.3434L1.65675 12L6 7.65675L10.3433 12L11.9999 10.3434L7.65661 6.00014L11.9999 1.65661Z"></path>
              </svg>
            </button>
          </dialog>  
        </div>
        <div class="c-popup__inner" v-else>
          <div class="c-reading-operation" ref="modalReadingFrame" :aria-hidden="!modelValue">
            <button class="close-button" @click="closeModal">閉じるボタン</button>
            <div class="operation-button">
              <button :class="{'operation-button-back': true, '-disabled': audioPlayer.currentTime <= 0}" @click="playBackBtn">5秒戻る</button>
              <button class="operation-button-main -pause" id="audioToggleButton" @click="toggleAudio">一時停止</button>
              <button :class="{'operation-button-forward': true, '-disabled': Math.ceil(audioPlayer.currentTime) >= Math.floor(audioPlayer.duration)}" @click="playForwardBtn">5秒進む</button>
            </div>
            <div class="operation-area" :aria-hidden="!exerciseSpeechStore.is_expand">
              <div class="operation-slider">
                <input type="range" min="0" max="100" class="operation-slider-range" @input="inputSeekValue" v-model="seekValue" :style="{background: 'linear-gradient(to right, rgb(41, 101, 247) '+ Math.ceil(seekValue) + '%, rgb(217, 217, 217) ' +  Math.ceil(seekValue) + '%)'}">
                <div class="operation-slider-duration">
                  <p class="operation-slider-duration-variable" id="currentTimeSpan">0:00</p>
                  <p class="operation-slider-duration-fixed" id="audioDuration">0:00</p>
                </div>
              </div>
              <div class="operation-speed">
                <p class="operation-speed-text">速さ</p>
                <div class="operation-speed-button">
                  <button class="operation-speed-slow" id="operationSpeedSlow" @click="decreaseSpeedBtn"><img src="@/styles/img/common/icon_play_slow.svg" width="12"></button>
                  <p class="operation-speed-status" id="speedStatus">{{ speedStatus }}</p>
                  <button class="operation-speed-fast" id="operationSpeedFast" @click="increaseSpeedBtn"><img src="@/styles/img/common/icon_play_fast.svg" width="12"></button>
                </div>
              </div>
            </div>
            <button class="expansion-button" :aria-expanded="exerciseSpeechStore.is_expand" type="button" @click="toggleExpand">閉じる</button>
          </div>
        </div>
      </template>
    </div>
  </transition>
</template>

<script setup lang="ts">
  import {ref, computed, onMounted, onUnmounted} from "vue"
  import { useExerciseSpeechStore } from "@/store/student/exercise_speech"
  import { useAccountStore } from '@/store/student/account_store';

  const props = withDefaults(
    defineProps<{
      modelValue?: boolean;
      isSelection?: boolean;
      subjectField: string;
      htmlText: string;
    }>(),
    {
      modelValue: false,
      isSelection: false,
    }
  );

  const emit = defineEmits<{
    (e: 'close'): void,
    (e: 'close-layer'): void,
    (e: 'update:model-value', value: boolean),
  }>()

  const exerciseSpeechStore = useExerciseSpeechStore()
  const isProcessing = ref(true)
  const isError = ref(false)
  const audioPlayer = ref(null)
  const seekValue = ref(0)

  const accountStore = useAccountStore()
  const speedStatus = ref("標準"); // NFR-5504 再生速度の状態を格納するref
  const modalReadingFrame = ref(null) // NFR-5677: to get reading modal frame

  onMounted(() => {
    // NFR-5677: add an click event to hide reading modal.
    document.addEventListener("click", hideReadingModal, true)
  })
  const modal = computed(() => {
    isProcessing.value = true
    isError.value = false
    if(props.modelValue){
      // fetch the text-to-speech audio
      exerciseSpeechStore.fetchTextToSpeech(props.subjectField, props.htmlText)
      .then(async(response) => {
        // create a blob file of audio file
        const blob = new Blob([response.data], { type: 'audio/mpeg' });
        // Create a link element
        const url = URL.createObjectURL(blob);
        // set the blob url for play downloaded audio
        audioPlayer.value.src = url
        audioPlayer.value.playbackRate = accountStore.playbackRate // NFR-5504 ストアから再生速度を取得して設定
        speedStatus.value = audioPlayer.value.playbackRate === 1.0 ? "標準" : `${audioPlayer.value.playbackRate}x`; // 速度状態表示を更新
        await audioPlayer.value.play()
      })
      .catch((error) => {
        isError.value = true
      })
      .finally(() => {
        isProcessing.value = false
      })
    }
    return props.modelValue
  })

  function hideReadingModal(event: Event): void {
    let mainTag = document.querySelector('main');
    // NFR-5677: if user click outside reading modal frame then close the reading modal
    if(modalReadingFrame.value && !modalReadingFrame.value.contains(event.target) &&  mainTag.classList.contains('-reading') && !isError.value){
      closeModal()
    }
  }
  // NFR-5579: close modal
  function closeModal() {
    stop()
    emit('close')
  }
  
  // NFR-5579: stop audio playing
  function stop() {
    audioPlayer.value.pause()
    audioPlayer.value.currentTime = 0
  }

  // Format time (minutes:secends)
  function formatTime(time: number) {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  // update playing time when audio are playing
  function timeUpdate() {
    const currentTimeSpan = document.getElementById("currentTimeSpan")
    const audioDuration = document.getElementById("audioDuration")
    if(currentTimeSpan && audioDuration) {
      const currentTime = audioPlayer.value.currentTime;
      const duration = audioPlayer.value.duration;
      currentTimeSpan.textContent = formatTime(currentTime);
      if(audioPlayer.value.duration){
        seekValue.value = Math.round((currentTime / duration) * 100);
        audioDuration.textContent = formatTime(duration);
      } else {
        seekValue.value = 0
      }
    }
  }

  // toggle player seek bar and speed button expansion
  function toggleExpand() {
    exerciseSpeechStore.is_expand = !exerciseSpeechStore.is_expand
  }

  // play back 5 seconds
  function playBackBtn() {
    audioPlayer.value.currentTime = Math.max(0, audioPlayer.value.currentTime - 5);
    // NFR-5696: after '始めから再生' shown if user click play back 5s btn then set the toggle button text
    const audioToggleButton: any = document.getElementById("audioToggleButton")
    if(audioPlayer.value.paused && audioToggleButton){
      audioToggleButton.innerHTML = "続きから再生"
    }
  }

  // toggle audio play and pause with play/pause button text changes
  function toggleAudio() {
    const audioToggleButton: any = document.getElementById("audioToggleButton")
    if(audioPlayer.value.paused){
      audioToggleButton.classList.remove("-play")
      audioToggleButton.classList.add("-pause")
      audioToggleButton.innerHTML = "一時停止"
      audioPlayer.value.play()
    } else {
      audioToggleButton.classList.remove("-pause")
      audioToggleButton.classList.add("-play")
      if(props.isSelection){
        audioToggleButton.innerHTML = "選択部分を再生"
      } else {
        audioToggleButton.innerHTML = "続きから再生"
      }
      audioPlayer.value.pause()
    }
  }
  // change play/pause button text at the end of playing
  function toggleAudioBtnText() {
    const audioToggleButton: any = document.getElementById("audioToggleButton")
    if(audioToggleButton){
      audioToggleButton.classList.remove("-pause")
      audioToggleButton.classList.add("-play")
      if(props.isSelection){
        audioToggleButton.innerHTML = "選択部分を再生"
      } else {
        // NFR-5696: set the toggle btn end text
        audioToggleButton.innerHTML = "始めから再生"
      }
    }
  }

  // play forward 5 seconds
  function playForwardBtn() {
    audioPlayer.value.currentTime = Math.min(audioPlayer.value.duration, audioPlayer.value.currentTime + 5);
  }

  // setting current playing time when click seek bar
  function inputSeekValue() {
    audioPlayer.value.currentTime = (seekValue.value / 100) * audioPlayer.value.duration;
    // NFR-5696: after '始めから再生' shown if user click seek bar then set the toggle button text
    const audioToggleButton: any = document.getElementById("audioToggleButton")
    if(audioPlayer.value.paused && audioToggleButton){
      audioToggleButton.innerHTML = "続きから再生"
    }
  }

  // decrease playing speed .25x each time click and also desabled click btn if speed is equal 0.25x
  async function decreaseSpeedBtn(){
    const operationSpeedSlow = document.getElementById("operationSpeedSlow")
    const operationSpeedFast = document.getElementById("operationSpeedFast")
    if (audioPlayer.value.playbackRate > 0.25) {
      audioPlayer.value.playbackRate -= 0.25;
      // NFR-5504 新しい再生速度をサーバーに保存
      await accountStore.fetchUpdateSettings({ "settings": { "playback_rate": audioPlayer.value.playbackRate}});
      accountStore.savePlaybackRate(audioPlayer.value.playbackRate)
      if(audioPlayer.value.playbackRate === 0.25){
        operationSpeedSlow.classList.add("-disabled")
      } else {
        operationSpeedFast.classList.remove("-disabled")
      }
      if(audioPlayer.value.playbackRate === 1.0){
        speedStatus.value = "標準"
      } else {
        speedStatus.value = `${audioPlayer.value.playbackRate}x`;
      }
    }
  }

  // increase playing speed .25x each time click and also desabled click btn if speed is equal 2.0x
  async function increaseSpeedBtn() {
    const operationSpeedSlow = document.getElementById("operationSpeedSlow")
    const operationSpeedFast = document.getElementById("operationSpeedFast")
    if (audioPlayer.value.playbackRate < 2.0) {
      audioPlayer.value.playbackRate += 0.25;
      // NFR-5504 新しい再生速度をサーバーに保存
      await accountStore.fetchUpdateSettings({ "settings": { "playback_rate": audioPlayer.value.playbackRate}});
      accountStore.savePlaybackRate(audioPlayer.value.playbackRate)
      if(audioPlayer.value.playbackRate === 2.0){
        operationSpeedFast.classList.add("-disabled")
      }else {
        operationSpeedSlow.classList.remove("-disabled")
      }
      if(audioPlayer.value.playbackRate === 1.0){
        speedStatus.value = "標準"
      } else {
        speedStatus.value = `${audioPlayer.value.playbackRate}x`;
      }
    }
  }
  onUnmounted(() => {
    // NFR-5677: revome event listner
    document.removeEventListener('click', hideReadingModal, true)
  })
</script>